import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Check, X, Trash2 } from "lucide-react";
import type { PracticeEntry } from "@/lib/db";
import { EntryDetailSheet } from "./EntryDetailSheet";
import { cn } from "@/lib/utils";

interface EntryCardProps {
  entry: PracticeEntry;
  onDelete: (id: string) => void;
  onFulfillPromise?: (id: string) => void;     // marks Ausgleich done
  onFulfillVersprechen?: (id: string) => void; // marks Versprechen done
  virtueNames?: string[];
}

// ── Deadline formatting ───────────────────────────────────────────────────────

function fmtDeadlineMeta(dateStr?: string): { text: string; overdue: boolean } | null {
  if (!dateStr) return null;
  const end = new Date(dateStr);
  const now = new Date();
  now.setSeconds(0, 0);

  const endDate = new Date(dateStr.slice(0, 10) + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((endDate.getTime() - today.getTime()) / 86400000);

  const dateLabel = end.toLocaleDateString("de-DE", { day: "numeric", month: "long" });

  if (end < now) return { text: `überfällig · ${dateLabel}`, overdue: true };
  if (diffDays === 0) return { text: `heute · ${dateLabel}`, overdue: false };
  if (diffDays === 1) return { text: `noch 1 Tag · ${dateLabel}`, overdue: false };
  return { text: `noch ${diffDays} Tage · ${dateLabel}`, overdue: false };
}

// ── Status circle ─────────────────────────────────────────────────────────────

interface StatusCircleProps {
  done: boolean;
  colorPending: string; // tailwind border color class
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
}

function StatusCircle({ done, colorPending, onClick, disabled }: StatusCircleProps) {
  return (
    <button
      type="button"
      disabled={disabled || done}
      onClick={onClick}
      className={cn(
        "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-all",
        done
          ? "border-emerald-500 bg-emerald-500"
          : cn("bg-transparent", colorPending, "cursor-pointer hover:scale-110 active:scale-95")
      )}
      aria-label={done ? "Erledigt" : "Als erledigt markieren"}
    >
      {done && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
    </button>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function EntryCard({
  entry,
  onDelete,
  onFulfillPromise,
  onFulfillVersprechen,
  virtueNames,
}: EntryCardProps) {
  const [deleting, setDeleting] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const isPositive = entry.type === "positive";
  const isMeditation = entry.type === "meditation";

  // Has Ausgleich when text OR date is set
  const hasAusgleich = !!(entry.versprechen || entry.versprechenBisDatum);
  const ausgleichDone = !hasAusgleich || !!entry.versprechenErfuellt;
  // Has Versprechen when text OR date is set
  const hasVersprechen = !!(entry.promise || entry.promiseBisDatum);
  const versprechenDone = !hasVersprechen || !!entry.promiseErfuellt;
  // All obligations resolved → card turns green
  const allDone = isPositive || isMeditation || (ausgleichDone && versprechenDone);

  const ausgleichDeadline = fmtDeadlineMeta(entry.versprechenBisDatum);
  const versprechenDeadline = fmtDeadlineMeta(entry.promiseBisDatum);

  const handleDelete = async () => {
    setDeleting(true);
    try { onDelete(entry.id); } finally { setDeleting(false); }
  };

  const detailSheet = (
    <EntryDetailSheet
      entry={entry}
      open={detailOpen}
      onOpenChange={setDetailOpen}
      virtueNames={virtueNames}
      onFulfillPromise={onFulfillPromise}
      onFulfillVersprechen={onFulfillVersprechen}
    />
  );

  // ── Positive card ──────────────────────────────────────────────────────────
  if (isPositive) {
    return (
      <>
        <Card
          className="border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-500/5 cursor-pointer active:scale-[0.99] transition-transform"
          onClick={() => setDetailOpen(true)}
        >
          <CardContent className="py-3 px-4 space-y-2">
            <div className="flex items-start gap-2">
              <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 mt-0.5">
                <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
              </div>
              <p className="flex-1 text-sm text-gray-900 dark:text-gray-100">{entry.text}</p>
            </div>

            {entry.widmung && (
              <p className="pl-7 text-xs text-emerald-700 dark:text-emerald-400/70">
                Widmung: {entry.widmung}
              </p>
            )}

            <div className="flex items-center justify-between pl-7">
              <div className="flex flex-wrap gap-1.5">
                {virtueNames?.map((name) => (
                  <Badge key={name} variant="outline" className="text-xs border-emerald-500/30 text-emerald-700 dark:text-emerald-400">
                    {name}
                  </Badge>
                ))}
              </div>
              <DeleteButton onDelete={handleDelete} deleting={deleting} />
            </div>
          </CardContent>
        </Card>
        {detailSheet}
      </>
    );
  }

  // ── Negative card ──────────────────────────────────────────────────────────
  const cardBorder = allDone
    ? "border-emerald-500/25 bg-emerald-500/4 dark:bg-emerald-500/4"
    : "border-rose-500/20 bg-rose-50/50 dark:bg-rose-500/5";

  return (
    <>
      <Card
        className={cn("cursor-pointer active:scale-[0.99] transition-transform", cardBorder)}
        onClick={() => setDetailOpen(true)}
      >
        <CardContent className="py-3 px-4 space-y-2">
          {/* Header */}
          <div className="flex items-start gap-2">
            <div className={cn(
              "flex h-5 w-5 shrink-0 items-center justify-center rounded-full mt-0.5",
              allDone ? "bg-emerald-500/20" : "bg-rose-500/20"
            )}>
              <X className={cn("h-3 w-3", allDone ? "text-emerald-500" : "text-rose-600 dark:text-rose-400")} />
            </div>
            <p className="flex-1 text-sm text-gray-900 dark:text-gray-100">{entry.text}</p>
          </div>

          {/* Ausgleich row */}
          {hasAusgleich && (
            <div className="pl-7 space-y-0.5">
              <div className="flex items-start gap-2">
                <StatusCircle
                  done={!!entry.versprechenErfuellt}
                  colorPending="border-amber-400/60 dark:border-amber-400/50"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!entry.versprechenErfuellt) onFulfillPromise?.(entry.id);
                  }}
                />
                <div className="flex-1 min-w-0">
                  <p className={cn(
                    "text-xs leading-snug",
                    entry.versprechenErfuellt
                      ? "text-emerald-600 dark:text-emerald-500 line-through opacity-70"
                      : "text-amber-700 dark:text-amber-400/80"
                  )}>
                    <span className="font-semibold text-[10px] uppercase tracking-wide mr-1 opacity-60">
                      Ausgleich
                    </span>
                    {entry.versprechen || <span className="italic opacity-60">Versprechen</span>}
                  </p>
                  {!entry.versprechenErfuellt && ausgleichDeadline && (
                    <p className={cn(
                      "text-[11px] mt-0.5",
                      ausgleichDeadline.overdue
                        ? "text-rose-500 dark:text-rose-400"
                        : "text-amber-600/60 dark:text-amber-400/50"
                    )}>
                      {ausgleichDeadline.text}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Versprechen row */}
          {hasVersprechen && (
            <div className="pl-7 space-y-0.5">
              <div className="flex items-start gap-2">
                <StatusCircle
                  done={!!entry.promiseErfuellt}
                  colorPending="border-vajra-blue/50 dark:border-vajra-cyan/40"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!entry.promiseErfuellt) onFulfillVersprechen?.(entry.id);
                  }}
                />
                <div className="flex-1 min-w-0">
                  <p className={cn(
                    "text-xs leading-snug",
                    entry.promiseErfuellt
                      ? "text-emerald-600 dark:text-emerald-500 line-through opacity-70"
                      : "text-vajra-blue dark:text-vajra-cyan/80"
                  )}>
                    <span className="font-semibold text-[10px] uppercase tracking-wide mr-1 opacity-60">
                      Versprechen
                    </span>
                    {entry.promise || <span className="italic opacity-60">Versprechen</span>}
                  </p>
                  {!entry.promiseErfuellt && versprechenDeadline && (
                    <p className={cn(
                      "text-[11px] mt-0.5",
                      versprechenDeadline.overdue
                        ? "text-rose-500 dark:text-rose-400"
                        : "text-vajra-blue/50 dark:text-vajra-cyan/40"
                    )}>
                      {versprechenDeadline.text}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Footer: virtue badges + delete */}
          <div className="flex items-center justify-between pl-7">
            <div className="flex flex-wrap gap-1.5">
              {virtueNames?.map((name) => (
                <Badge key={name} variant="outline" className="text-xs border-rose-500/30 text-rose-700 dark:text-rose-400">
                  {name}
                </Badge>
              ))}
            </div>
            <DeleteButton onDelete={handleDelete} deleting={deleting} />
          </div>
        </CardContent>
      </Card>
      {detailSheet}
    </>
  );
}

// ── Shared delete button ──────────────────────────────────────────────────────

function DeleteButton({ onDelete, deleting }: { onDelete: () => void; deleting: boolean }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-gray-400 dark:text-muted-foreground hover:text-red-500 dark:hover:text-red-400"
          disabled={deleting}
          onClick={(e) => e.stopPropagation()}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Eintrag löschen?</AlertDialogTitle>
          <AlertDialogDescription>
            Dieser Eintrag wird unwiderruflich gelöscht.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
          <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">
            Löschen
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
